import { RowDataUpdatedEvent, SelectionChangedEvent } from "ag-grid-community";
import { UserEntity } from "domain/entity/User/UserEntity";
import _ from "lodash";
import { MessageConstant } from "presentation/constant/MessageConstant";
import { INITIAL_USER_COL_DEF, transferRowData } from "presentation/constant/User/UserColumnDefinition";
import { UserConStant } from "presentation/constant/User/UserConstant";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useUserVM } from "presentation/hook/User/useUserVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useUserTracked } from "presentation/store/User/UserProvider";
import { ConfirmDeleteModal } from "presentation/view/components/ConfirmDeleteModal";
import NbisTable from "presentation/view/components/TableWrapper/NbisTable";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader } from "veronica-ui-component/dist/component/core";

const UserTablePanel: React.FC = () => {
    const [userState] = useUserTracked();
    const [anaInfoSate] = useANAInfoTracked();
    const messageBarVM = useMessageBarVM();
    const userVM = useUserVM();
    const USER_CONSTANT = UserConStant.user;
    const { isTabularDataActive, currentSelectedData, lastEditRowId } = userState.userState;
    const allFormState = userState.userState.allFormState;
    const [isShowDelete, setIsShowDelete] = useState<boolean>(false);
    const [deletedUser, setDeletedUser] = useState<UserEntity>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [onTableSelectionClicked, setOnTableSelectionClicked] = useState<boolean>(false);
    let gridRef: any = useRef();
    const [showOverlay, setShowOverlay] = useState<boolean>(false);

    useEffect(() => {
        if (!onTableSelectionClicked) return;

        gridRef.current?.gridRef.current.api?.deselectAll();
    }, [onTableSelectionClicked])

    useEffect(() => {
        if (!onTableSelectionClicked) {
            gridRef.current?.gridRef.current.api?.deselectAll();
        }
    })

    useEffect(() => {
        setShowOverlay(
            userState.users && userState.users.length ? false : true);
    }, [userState.isLoading, userState.users]);

    const isRowSelectable = useCallback((params: any) => {
        return !(userState.userState.isAdd || userState.userState.isEditable);
    }, [userState.userState.isAdd, userState.userState.isEditable]);

    const onRowDataUpdated = useCallback((params: RowDataUpdatedEvent) => {
        if (userState.isReselectRows && lastEditRowId &&
            !_.isEmpty(userState.users)) {
            params?.api?.forEachNode((node: any) => {
                if (lastEditRowId === node?.data?.name) {
                    node.setSelected(true);
                }
            });
        }
    }, [lastEditRowId, userState.isReselectRows, userState.users])


    const handleSelectionChange = useCallback((e: SelectionChangedEvent) => {
        setOnTableSelectionClicked(true);
        const selectedRows = e.api.getSelectedRows();
        userVM.onSelectedUsers(selectedRows);
    }, [userVM])

    const handleAddClick = useCallback(() => {
        userVM.onUserAddClick(anaInfoSate.buToken?.operatingCompany);
    }, [anaInfoSate.buToken?.operatingCompany, userVM])

    const handleRowDoubleClick = useCallback((user: UserEntity) => {
        userVM.onUserRowDoubleClick(user);
    }, [userVM])

    const handleRowDeleteClick = useCallback(async (user: UserEntity) => {
        setIsShowDelete(true);
        setDeletedUser(user);
    }, []);

    const handleCancel = () => {
        setIsLoading(true);
        setIsShowDelete(false);
        userVM.loadAllUsers().then(data => {
            setIsLoading(false);
        });
    }

    const handleDelete = () => {
        setIsLoading(true);
        setIsShowDelete(false);
        if (!deletedUser) return;

        userVM.onUserRowDeleteClick(deletedUser).then((data) => {
            if (allFormState && allFormState["DeleteUserFail"]) {
                messageBarVM.showError(allFormState["DeleteUserFail"]?.toString());
            } else {
                messageBarVM.showSuccess(MessageConstant.common.DELETED_DATA_SUCCESSFUL)
                userVM.loadAllUsers().then(data => {
                    setIsLoading(false);
                });
            }
        }).catch((error) => {
            setIsLoading(false);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    const isCopyBtnDisable = useCallback(() => {
        if (anaInfoSate.allowCreate && currentSelectedData && currentSelectedData.name) return false
        return true;
    }, [anaInfoSate.allowCreate, currentSelectedData])

    const handleCopy = useCallback(() => {
        userVM.onUserRenewClick();
    }, [userVM])

    const memoTableBtns = useMemo(() => {
        const tableBtns = [];
        if (anaInfoSate.allowCreate) {
            tableBtns.push({
                id: 'onCopyButton',
                icon: 'Icon-copy',
                title: 'Copy User',
                disable: isCopyBtnDisable
            })
        }
        return tableBtns
    }, [isCopyBtnDisable, anaInfoSate.allowCreate])

    const memoUserTable = useMemo(() => {

        return <NbisTable
            id='user-table'
            columns={INITIAL_USER_COL_DEF?.slice()}
            headerLabel={USER_CONSTANT.USER}
            headerActionButtons={memoTableBtns}
            onAddClick={handleAddClick}
            handleSelectionChanged={handleSelectionChange}
            onRowDoubleClick={(e: any) => handleRowDoubleClick(e.data)}
            data={transferRowData(userState.users ?? [])}
            showPaginator={false}
            editable={false}
            showAddIcon={anaInfoSate.allowCreate}
            showDeleteButton={anaInfoSate.allowDelete}
            showActionColumn={true}
            showReloadIcon={false}
            selectionMode={true}
            isNewColumnSetting={true}
            isScrollHighlighted={true}
            isRowHighligted={true}
            onDelete={(deleteUser: UserEntity) => handleRowDeleteClick(deleteUser)}
            onCopyButton={handleCopy}
            rowSelection={"single"}
            gridHeight="customHeight"
            customHeight="calc(100vh - 72px)"
            handleRowSelectable={isRowSelectable}
            onRowDataUpdated={(params: RowDataUpdatedEvent) => onRowDataUpdated(params)}
            showDefaultOverlayNoRowsTemplate={showOverlay}
        />;
    }, [USER_CONSTANT.USER, memoTableBtns, handleAddClick, handleSelectionChange, userState.users, anaInfoSate.allowCreate, anaInfoSate.allowDelete, handleCopy, isRowSelectable, showOverlay, handleRowDoubleClick, handleRowDeleteClick, onRowDataUpdated])

    useEffect(() => {
        userVM.onShowLoading();
        const initialScreen = async () => {
            try {
                const results = await Promise.allSettled([
                    userVM.loadAllUsers(),
                    userVM.loadDropdownOptions()
                ]);
                results?.forEach((result, index) => {
                    if (index === 0 && result.status === 'fulfilled') {

                    } else if (index === 1 && result.status === 'rejected') {

                    }
                })
            } catch (error) {
            }
        }
        initialScreen().then((data) => {
            userVM.onHideLoading();
        }).catch(error => {
            userVM.onHideLoading();
        });
    }, [userVM])

    if (userState.isLoading) return <Loader Indicator="Stripe" size="Large" />;

    return <>{isTabularDataActive && <TableWrapper>{memoUserTable}</TableWrapper>}
        {isLoading && <Loader Indicator="Spinner" size="Medium" />}
        <ConfirmDeleteModal headerText={`${WorkspaceConstant.Common.BUTTON_DELETE} ${UserConStant.user.USER}`}
            contentText={`${MessageConstant.common.DELETE_ALERT_TITLE}\n${MessageConstant.common.DELETE_ALERT_MESSAGE}`}
            visible={isShowDelete}
            onCancelClick={handleCancel}
            onDeleteClick={handleDelete}
        />
    </>;
}

export default UserTablePanel;